import { User } from '@core/domain/User/types';
import StorageFactory, { ICache } from '@infrastructure/cache';
import AbstractModuleFactory from '@core/shared/module-factory';
import { UIAdaptedGeneralSetting } from '@core/domain/Settings/interfaces';

type EventHandler<T> = (profile: T) => any;
export interface IUserSessionRespository {
  getToken(): Promise<Nullable<string>>;
  saveToken(token: string): Promise<void>;
  getRefreshToken(): Promise<Nullable<string>>;
  saveRefreshToken(token: string): Promise<void>;
  getProfile(): Promise<Nullable<User>>;
  saveProfile(profile: User): Promise<void>;
  removeSession(): Promise<void>;
  subscribe(handler: EventHandler<User>): void;
  setSettingPreferences(settings: UIAdaptedGeneralSetting): Promise<void>;
}

export class UserSessionRespository implements IUserSessionRespository {
  private subscribers: EventHandler<User>[] = [];

  constructor(private storage: ICache) {
    this.storage = storage;
  }
  async setSettingPreferences(settings: UIAdaptedGeneralSetting): Promise<void> {
    await this.storage.setItem('theme-mode', settings.theme);
    await this.storage.setItem('i18nextLng', settings.language);
  }

  subscribe(handler: EventHandler<User>): void {
    this.subscribers.push(handler);
  }

  async getToken(): Promise<Nullable<string>> {
    const token = await this.storage.getItem('token');
    return token ?? null;
  }

  async saveToken(token: string): Promise<void> {
    return await this.storage.setItem('token', token);
  }

  async getRefreshToken(): Promise<Nullable<string>> {
    const token = await this.storage.getItem('refreshToken');
    return token ?? null;
  }
  async saveRefreshToken(token: string): Promise<void> {
    return await this.storage.setItem('refreshToken', token);
  }

  async getProfile(): Promise<Nullable<User>> {
    const str = await this.storage.getItem('principal');
    if (str) return JSON.parse(str) as User;
    return null;
  }

  async saveProfile(profile: User): Promise<void> {
    this.subscribers.forEach(subscriber => subscriber(profile));
    await this.storage.setItem('principal', JSON.stringify(profile));
  }

  async removeSession(): Promise<void> {
    await this.storage.clear();
  }
}

/**
 * ### UserSession Respository module dependency factory.
 * Abstract Factory that create instance for for in-memory
 * or for local UserSession Repository.
 *
 * @author <pino0071@gmail.com> Jose Aburto
 */
class UserSessionRespositoryFactory extends AbstractModuleFactory {
  createLocalUserSessionRepository(): IUserSessionRespository {
    return new UserSessionRespository(StorageFactory.getAsyncGlobalInstance());
  }
  createInMemoryUserSessionRepository(): IUserSessionRespository {
    return this.ensureInstance('in-memory', () => new UserSessionRespository(StorageFactory.createSessionStorage()));
  }

  createInSessionUserSessionRepository(): IUserSessionRespository {
    return new UserSessionRespository(StorageFactory.getAsyncGlobalInstance());
  }

  static new(): UserSessionRespositoryFactory {
    return new UserSessionRespositoryFactory();
  }
}

export default UserSessionRespositoryFactory.new();
